import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/apiClient";
import { Bell, CheckCheck, Mail } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "hace un momento";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/mine");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (err) {
      console.error("Error al cargar notificaciones:", err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const markRead = async (n) => {
    if (n.read) return;
    try {
      await api.post(`/notifications/${n.id}/read`);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (err) {
      console.error("Error al marcar notificación como leída:", err);
    }
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/mine/read-all");
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch (err) {
      console.error("Error al marcar todas como leídas:", err);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 rounded-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-200">
          <Bell className="h-5 w-5 text-slate-500" />
          {unread > 0 && (
            <span data-testid="notification-unread-count"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 rounded-sm p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
          <span className="text-sm font-bold tracking-tight">Notificaciones</span>
          {unread > 0 && (
            <button data-testid="mark-all-read-btn" onClick={markAll}
              className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1 transition-colors duration-200">
              <CheckCheck className="h-3.5 w-3.5" /> Marcar todas
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && (
            <div className="px-4 py-10 text-center text-slate-400 text-sm">
              <Mail className="h-8 w-8 mx-auto mb-2 opacity-40" />
              Sin notificaciones
            </div>
          )}
          {items.map((n) => (
            <button key={n.id} data-testid={`notification-item-${n.id}`} onClick={() => markRead(n)}
              className={`w-full text-left px-4 py-3 border-b border-border last:border-0 transition-colors duration-150 hover:bg-slate-50 dark:hover:bg-slate-800 ${n.read ? "opacity-60" : ""}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-red-600 shrink-0" />}
                <div className={`flex-1 min-w-0 ${n.read ? "pl-4" : ""}`}>
                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 leading-snug">{n.subject}</div>
                  <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">{n.body}</div>
                  <div className="text-[10px] text-slate-400 mt-1">{timeAgo(n.timestamp)}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
